/**
 * A line that says what just happened, and offers to take it back.
 *
 * Docked above the camera button rather than floating over the middle of the
 * screen: it never covers what you were looking at, and it never asks to be
 * dismissed — it leaves on its own.
 */

import { useEffect, useRef } from "react";

import { Icon } from "./Icon";
import type { IconName } from "./Icon";

import "./Toast.css";

interface ToastProps {
  message: string;
  icon?: IconName;
  /** Milliseconds until it leaves on its own. */
  duration: number;
  action?: { label: string; onAction: () => void };
  onDismiss: () => void;
}

export function Toast({ message, icon = "undo", duration, action, onDismiss }: ToastProps) {
  /*
   * The countdown runs from when the toast appeared, not from the last render.
   *
   * `onDismiss` is a fresh closure on every render of the shell, so having the
   * effect depend on it meant *any* re-render during those five seconds tore
   * the timer down and started a new one. Nothing re-rendered the shell mid-toast
   * until background storage writes started reporting failures, and then a
   * five-second undo could quietly become fifteen — or never lapse at all under
   * a stream of updates.
   *
   * The handler goes in a ref so the effect can depend on `duration` alone and
   * still call the current one.
   */
  const dismiss = useRef(onDismiss);
  useEffect(() => {
    dismiss.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    const timer = setTimeout(() => dismiss.current(), duration);
    return () => clearTimeout(timer);
  }, [duration]);

  return (
    // Polite, not assertive: this is never urgent enough to interrupt someone
    // mid-sentence in a screen reader.
    <div className="toast" role="status" aria-live="polite">
      <Icon name={icon} className="toast__icon" />
      <span className="toast__message">{message}</span>
      {action && (
        <button className="toast__action" type="button" onClick={action.onAction}>
          {action.label}
        </button>
      )}
    </div>
  );
}
